import type { IResizeHandler } from '@/engine/app/interfaces';
import { AutoEventSubscription } from '@/engine/Utilities/AutoEventSubscription';
import { type IDisposable } from '@/engine/Utilities/IDisposable';

/**
 * Represents a ViewPortResizer that handles resizing of a container element.
 */
export class ViewPortResizer {
  private readonly _container: HTMLElement;

  private readonly _resizeHandler: IResizeHandler;

  private readonly _subscription: IDisposable;

  public constructor(container: HTMLElement, resizeHandler: IResizeHandler) {
    this._container = container;
    this._resizeHandler = resizeHandler;
    this._setSize(this._container.clientWidth, this._container.clientHeight);
    this._subscription = new AutoEventSubscription(window, 'resize', this._onResize.bind(this));
  }

  public dispose(): void {
    this._subscription.dispose()
  }

  private _onResize(): void {
    this._setSize(this._container.clientWidth, this._container.clientHeight);
  }

  private _setSize(width: number, height: number): void {
    this._resizeHandler.handleViewPortResize(width, height);
  }

  public resize(): void {
    this._onResize();
  }

  /**
   * Sets the size of the view port explicitly. Used for report generation.
   * @param width - The width of the view port in pixels.
   * @param height - The height of the view port in pixels.
   */
  public setSizeExplicitly(width: number, height: number): void {
    this._container.style.width = `${width}px`;
    this._container.style.height = `${height}px`;
    this._setSize(width, height);
  }

  /**
   * Resets the size of the view port container to its original size.
   */
  public resetSize(): void {
    this._container.style.width = '';
    this._container.style.height = '';
    this._onResize();
  }
}
